// Unlayer renders the email design inside an iframe served from https://editor.unlayer.com — a
// PUBLIC origin. Since Chrome 142 (Local Network Access) a public origin may not load subresources
// from a private/local address space without an explicit user permission grant, which a
// cross-origin iframe cannot obtain. Every on-prem deployment therefore serves its API from a
// private IP that the editor iframe is forbidden to fetch, so re-homed template images render blank
// there while working fine on platform.kuika.com (public API host, no LNA check).
//
// The parent designer page is itself inside that private space, so it CAN read the resource. These
// helpers let the canvas swap resource URLs for self-contained "data:" URIs before handing the
// design to Unlayer, and swap them back before the template is persisted — so no image request
// ever crosses the public/private boundary and the stored template shape is unchanged.

// A resource runtime URL as it appears in a stored design/body. Case-insensitive because the two
// producers disagree: the backend re-homing service emits "/v1/api/Resource/runtime/byid" while the
// frontend upload path emits "/resource/runtime/byid". Trailing query params (Unlayer appends
// &width/&height in exported HTML) are deliberately NOT captured — the id is all we need, and
// keeping the match minimal makes the reverse rewrite exact.
const RESOURCE_RUNTIME_URL_PATTERN =
  /https?:\/\/[^\s"'<>()\\]*?\/v1\/api\/resource\/runtime\/byid\?resourceid=[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

const RESOURCE_ID_PATTERN = /resourceid=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

// Any "data:" image URI, used only by the pre-persist guard below.
const INLINED_IMAGE_PATTERN = /data:image\/[a-z0-9.+-]+;base64,/i;

// One image that was swapped out of a design so Unlayer could render it without a network request.
export interface IInlinedTemplateImage {
  // The exact substring that appeared in the stored design, restored verbatim on save.
  resourceUrl: string;
  // The resource id parsed out of that URL. The URL is re-resolved against the CURRENT backend
  // origin before fetching, because an on-prem API host often differs from the one that saved it.
  resourceId: string;
  // "data:<mime>;base64,<...>" handed to Unlayer in place of resourceUrl. collectResourceImageRefs
  // leaves this empty and the caller fills it in once the bytes are fetched; an empty value means
  // the fetch failed and both rewrites below skip the ref entirely.
  dataUrl: string;
}

// Pairs a freshly uploaded image's stored URL with the data URI handed to Unlayer in its place, so
// the save path can rewrite it back exactly like a load-time substitution. Returns null if the URL
// is not a resource runtime URL, letting the caller fall back to passing the URL through untouched.
export const buildInlinedImageRef = (resourceUrl: string, dataUrl: string): IInlinedTemplateImage | null => {
  const idMatch: RegExpMatchArray | null = resourceUrl.match(RESOURCE_ID_PATTERN);
  if (!idMatch || !dataUrl) return null;
  return { resourceUrl, resourceId: idMatch[1], dataUrl };
};

// Distinct resource URLs referenced anywhere in the given design/body text, each with its parsed
// id. A URL repeated across rows is returned once so it is fetched once.
export const collectResourceImageRefs = (text: string): IInlinedTemplateImage[] => {
  if (!text) return [];

  const seen: Set<string> = new Set<string>();
  const refs: IInlinedTemplateImage[] = [];

  const matches: string[] = text.match(RESOURCE_RUNTIME_URL_PATTERN) ?? [];
  matches.forEach((resourceUrl: string): void => {
    if (seen.has(resourceUrl)) return;
    const idMatch: RegExpMatchArray | null = resourceUrl.match(RESOURCE_ID_PATTERN);
    if (!idMatch) return;
    seen.add(resourceUrl);
    refs.push({ resourceUrl, resourceId: idMatch[1], dataUrl: "" });
  });

  return refs;
};

// Replaces every resource URL with its data URI. Refs whose fetch failed (empty dataUrl) are left
// untouched, so one unreadable image degrades to today's behaviour instead of blanking the design.
export const inlineResourceImages = (text: string, refs: IInlinedTemplateImage[]): string => {
  if (!text) return text;

  return refs.reduce((accumulated: string, ref: IInlinedTemplateImage): string => {
    if (!ref.dataUrl) return accumulated;
    return accumulated.split(ref.resourceUrl).join(ref.dataUrl);
  }, text);
};

// Restores the original resource URLs. Runs on the JSON-encoded design and body strings: a data URI
// contains only base64 plus ":;,/+=", none of which JSON.stringify escapes, so the substring is
// present verbatim in the encoded form and a plain replace is exact.
export const revertInlinedResourceImages = (text: string, refs: IInlinedTemplateImage[]): string => {
  if (!text) return text;

  return refs.reduce((accumulated: string, ref: IInlinedTemplateImage): string => {
    if (!ref.dataUrl) return accumulated;
    return accumulated.split(ref.dataUrl).join(ref.resourceUrl);
  }, text);
};

// Guard for the persist path. A design/body that still carries base64 image bytes after the reverse
// rewrite means a ref was missed; persisting it would bloat the row and break mail rendering (mail
// clients block data: images, and the server's CID inliner only recognises resource URLs). Callers
// must refuse the save rather than write that.
export const containsInlinedImage = (text: string): boolean => {
  if (!text) return false;
  return INLINED_IMAGE_PATTERN.test(text);
};
